import dayjs, { type Dayjs } from "dayjs";

import {
  type DayContent,
  type ProgramAutomation,
  type ProgramAutomationPackageRange,
  type ProgramAutomationScheduledTask,
  ProgramAutomationScheduleAnchor,
} from "@fitness-app/data-models/entities/ProgramAutomation";

export type ScheduleTree = {
  day: DayContent;
  dayOffset: number;
  dayDate: string;
};

/**
 * Resolves the calendar date for a package-anchored task. Returns null when the timing
 * is week/day-based or the package range required by the anchor is missing/invalid -
 * callers decide their own fallback.
 */
export const resolvePackageAnchoredDate = (
  scheduleTiming: ProgramAutomationScheduledTask["scheduleTiming"],
  packageRange: ProgramAutomationPackageRange | null | undefined,
): Dayjs | null => {
  if (!scheduleTiming || scheduleTiming.anchor === ProgramAutomationScheduleAnchor.WeekDay) {
    return null;
  }

  const packageStartDate = packageRange?.startDate ? dayjs(packageRange.startDate).startOf("day") : null;
  const packageEndDate = packageRange?.endDate ? dayjs(packageRange.endDate).startOf("day") : null;

  if (scheduleTiming.anchor === ProgramAutomationScheduleAnchor.PackageStart && packageStartDate?.isValid()) {
    return packageStartDate.add(scheduleTiming.offsetDays, "day");
  }

  if (scheduleTiming.anchor === ProgramAutomationScheduleAnchor.PackageEnd && packageEndDate?.isValid()) {
    return packageEndDate.add(scheduleTiming.offsetDays, "day");
  }

  if (
    scheduleTiming.anchor === ProgramAutomationScheduleAnchor.PackageProgress &&
    packageStartDate?.isValid() &&
    packageEndDate?.isValid()
  ) {
    const durationDays = Math.max(packageEndDate.diff(packageStartDate, "day"), 0);
    const offsetDays = Math.round((durationDays * scheduleTiming.percent) / 100);
    return packageStartDate.add(offsetDays, "day").add(scheduleTiming.offsetDays ?? 0, "day");
  }

  return null;
};

export const createAutomationSchedule = (
  automation?: ProgramAutomation | null,
  startDate?: Dayjs | null,
  packageRange?: ProgramAutomationPackageRange | null,
) => {
  if (!automation || !startDate) {
    return [];
  }
  const firstDay = dayjs(startDate);
  const weeks = Object.entries(automation.schedule)
    .map(([weekStr, week]) => {
      const weekNumber = Number(weekStr) - 1;

      return Object.entries(week)
        .map(([dayStr, day]) => {
          const dayNumber = Number(dayStr) - 1;
          const dayOffset = dayNumber + weekNumber * 7;
          const resolvedItems = day.items
            .map((item) => ({
              item,
              dayDate: resolvePackageAnchoredDate(item.scheduleTiming, packageRange) ?? firstDay.add(dayOffset, "day"),
            }))
            .filter(({ dayDate }) => dayDate.isValid());

          return resolvedItems.map(({ item, dayDate }) => ({
            day: {
              ...day,
              items: [item],
            },
            dayOffset,
            dayDate: dayDate.format("DD-MM-YYYY"),
          }));
        })
        .flat()
        .filter((item): item is ScheduleTree => !!item.day);
    })
    .flat();

  const packageAnchoredTasks = (automation.packageAnchoredTasks ?? [])
    .map((item) => ({
      item,
      dayDate: resolvePackageAnchoredDate(item.scheduleTiming, packageRange) ?? firstDay,
    }))
    .filter(({ dayDate }) => dayDate.isValid())
    .map<ScheduleTree>(({ item, dayDate }) => ({
      day: {
        id: `package-${item.id}`,
        items: [item],
      },
      dayOffset: 0,
      dayDate: dayDate.format("DD-MM-YYYY"),
    }));

  return [...weeks, ...packageAnchoredTasks];
};
